import React from 'react';
import { SocialPost } from '../../../../types/socialMediaPlanner';
import { format } from 'date-fns';
import PlatformIcon from '../../../../components/common/PlatformIcon';

interface QueueViewProps {
  posts: SocialPost[];
  onEditPost: (post: SocialPost) => void;
  onDeletePost: (postId: string) => void;
}

/**
 * Queue View Component
 * Displays scheduled and draft posts in a list format
 */
const QueueView: React.FC<QueueViewProps> = ({ posts, onEditPost, onDeletePost }) => {
  // Using the shared PlatformIcon component instead of local function

  // Group posts by status
  const scheduledPosts = posts.filter(post => post.status === 'scheduled');
  const draftPosts = posts.filter(post => post.status === 'draft');

  return (
    <div className="space-y-8">
      {/* Scheduled Posts */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Scheduled Posts</h2>
        {scheduledPosts.length === 0 ? (
          <div className="bg-white p-4 rounded shadow-sm text-gray-500">
            No scheduled posts yet.
          </div>
        ) : (
          <div className="space-y-4">
            {scheduledPosts.map((post) => (
              <div key={post.id} className="bg-white p-4 rounded shadow-sm border-l-4 border-blue-500">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex space-x-2">
                    {post.platforms.map((platform, idx) => (
                      <span key={idx}><PlatformIcon platform={platform} /></span>
                    ))}
                  </div>
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => onEditPost(post)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Edit
                    </button>
                    <button 
                      onClick={() => window.confirm('Are you sure you want to delete this post?') && onDeletePost(post.id || '')}
                      className="text-red-500 hover:text-red-700"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                
                <p className="mb-4">{post.content}</p>
                
                {post.scheduled_for && (
                  <div className="flex items-center text-sm text-gray-500">
                    <div className="inline-flex items-center justify-center w-4 h-4 mr-1">
                      <div className="w-3.5 h-3.5 border border-current rounded-full relative">
                        <div className="absolute top-1/2 left-1/2 w-1 h-1.5 bg-current transform -translate-x-0.5 -translate-y-1/2 origin-bottom rotate-0"></div>
                        <div className="absolute top-1/2 left-1/2 w-1 h-1 bg-current transform -translate-x-0.5 -translate-y-0 origin-bottom rotate-90"></div>
                      </div>
                    </div>
                    Scheduled for: {format(new Date(post.scheduled_for), 'MMM d, yyyy h:mm a')}
                  </div>
                )}

                {post.hashtags && post.hashtags.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {post.hashtags.map((tag, i) => (
                      <span key={i} className="text-xs bg-gray-100 px-2 py-1 rounded-full">
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Draft Posts */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Draft Posts</h2>
        {draftPosts.length === 0 ? (
          <div className="bg-white p-4 rounded shadow-sm text-gray-500">
            No draft posts yet.
          </div>
        ) : (
          <div className="space-y-4">
            {draftPosts.map((post) => (
              <div key={post.id} className="bg-white p-4 rounded shadow-sm border-l-4 border-gray-300">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex space-x-2">
                    {post.platforms.map((platform, idx) => (
                      <span key={idx}><PlatformIcon platform={platform} /></span>
                    ))}
                    <span className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded-full">Draft</span>
                  </div>
                  <div className="flex space-x-2">
                    <button 
                      onClick={() => onEditPost(post)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Edit
                    </button>
                    <button 
                      onClick={() => window.confirm('Are you sure you want to delete this post?') && onDeletePost(post.id || '')}
                      className="text-red-500 hover:text-red-700"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                
                <p className="mb-2">{post.content}</p>

                {post.hashtags && post.hashtags.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {post.hashtags.map((tag, i) => (
                      <span key={i} className="text-xs bg-gray-100 px-2 py-1 rounded-full">
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default QueueView;
